import {
  Box,
  withStyles,
  TransactionsLists,
  removeWhiteSpaceURI,
  capitalizeFirstLetter,
  Tooltip,
  UtilButton,
  SupportedCurrencies,
  formattedCurrency,
  TransactionTypesOptions,
} from 'vopay';
import { FunctionComponent, useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useClientData } from '@/providers/ClientData.provider';
import useFetchTransactionsHistory from '@/hooks/useApi/useFetchTransactionsHistory';
import { format, parse, addDays, subWeeks } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useNavMenu } from '@/providers/NavMenu.provider';
import TransactionStatus from '@/components/UI/TransactionStatus/TransactionStatus';
import Table from '../../components/Table';
import { formatInTimeZone } from 'date-fns-tz';
import styles from './RecentTransactions.module.scss';
import NoRecentTransactions from './NoRecentTransactions';
import { Title } from 'UI';
import Throbber from '../../components/Throbber';

const cn = withStyles(styles);

type RecentTransactionsProps = {
  currency: SupportedCurrencies;
};

type FC = FunctionComponent<RecentTransactionsProps>;

const RecentTransactions: FC = ({ currency }) => {
  const { response } = useClientData();
  const { t, i18n } = useTranslation('translations', { keyPrefix: 'HomepageRevamp.Widgets.RecentTransactions' });
  const { t: tGlobal } = useTranslation('translations');
  const { t: tType } = useTranslation('translations', { keyPrefix: 'Global.TransactionTypes' });
  const { fetching, fetchTransactionsHistory } = useFetchTransactionsHistory();
  const [transactionsData, setTransactionsData] = useState<TransactionsLists[]>([]);
  const { onSubNavClick } = useNavMenu();

  useEffect(() => {
    const fetchData = async () => {
      const startDateTime = formatInTimeZone(subWeeks(new Date(), 1), 'UTC', 'yyyy-MM-dd HH:mm:ss');
      const currentDateTime = formatInTimeZone(addDays(new Date(), 1), 'UTC', 'yyyy-MM-dd HH:mm:ss');

      try {
        const res = await fetchTransactionsHistory({
          PortalUser: response.LoginUserName,
          StartDateTime: startDateTime,
          EndDateTime: currentDateTime,
          Currency: currency,
          Limit: 5,
        });
        setTransactionsData(Object.values(res.Transactions || {}));
      } catch (err) {
        console.error(err);
      }
    };
    fetchData();
  }, [response.LoginUserName, currency]);

  const columns = [
    { columnKey: 'recent-id', title: t('id') },
    { columnKey: 'recent-type', title: t('type') },
    { columnKey: 'recent-date', title: t('date') },
    { columnKey: 'recent-amount', title: t('amount') },
    { columnKey: 'recent-status', title: t('status') },
  ];

  const TransactionTypeMap: Partial<Record<TransactionTypesOptions | 'eLinx', string>> = {
    'EFT Withdrawal': tType('EFT Withdrawal'),
    'EFT Funding': tType('EFT Funding'),
    'VoPayInstant Withdraw': tType('VoPayInstant Withdraw'),
    Refund: tType('Refund'),
    'Currency Conversion': tType('Currency Conversion'),
    'Interac Money Request': tType('Interac Money Request'),
    'Interac Bulk Payout': tType('Interac Bulk Payout'),
    'Inbound e-Transfer': tType('Inbound e-Transfer'),
    'Visa Direct Push': tType('Visa Direct Push'),
    'Credit Card': tType('Credit Card'),
    'Account Transfer': tType('Account Transfer'),
    Reversal: tType('Reversal'),
    'Auto Balance Transfer': tType('Auto Balance Transfer'),
    'Bill Pay': tType('Bill Pay'),
    'Virtual Account Deposit': tType('Virtual Account Deposit'),
    'Virtual Account Funding': tType('Virtual Account Funding'),
    'Virtual Account Withdraw': tType('Virtual Account Withdraw'),
    'Virtual Account Wire': tType('Virtual Account Wire'),
    'Other Funding': tType('Other Funding'),
    'Other Withdrawal': tType('Other Withdrawal'),
    'ACH Withdrawal': tType('ACH Withdrawal'),
    'ACH Funding': tType('ACH Funding'),
    'RTP Withdrawal': tType('RTP Withdrawal'),
    'Fedwire Withdrawal': tType('Fedwire Withdrawal'),
    'Fedwire Funding': tType('Fedwire Funding'),
    'Crossborder Withdrawal': tType('Crossborder Withdrawal'),
    eLinx: tType('eLinx'),
    'PayPal Withdrawal': tType('PayPal Withdrawal'),
    'Venmo Withdrawal': tType('Venmo Withdrawal'),
    'Google Pay': tType('Google Pay'),
  };

  const rows = useMemo(() => {
    return transactionsData.map((data) => ({
      rowKey: data.TransactionID,
      [t('id')]: (
        <Box gap="sm" ai="center">
          <Link
            className={styles.transactionIDLink}
            to={{
              pathname: `/transactions-history/details/${removeWhiteSpaceURI(data.TransactionType)}/${
                data.TransactionID
              }/${data.Currency}`,
              state: {
                transactionId: data.TransactionID,
                hasErrorInfo: data.TransactionStatus === 'failed',
                failureReason: data.TransactionFailureReason,
                clientAccountId: data.ClientAccountID,
              },
            }}
          >
            {data.TransactionID}
          </Link>
        </Box>
      ),
      [t('type')]: TransactionTypeMap[data.TransactionType],
      [t('date')]: format(parse(data.TransactionDateTime, 'yyyy-MM-dd HH:mm:ss', new Date()), 'MMM dd, yyyy', { locale: i18n.language === 'fr' ? fr : undefined }),
      [t('amount')]:
        data.DebitAmount !== '0.00'
          ? `${currency} ${formattedCurrency(data.DebitAmount)}`
          : `${currency} ${formattedCurrency(data.CreditAmount)}`,
      [t('status')]: (
        <Box gapSize="10px">
          <TransactionStatus
            status={data.TransactionStatus}
            label={
              capitalizeFirstLetter(
                tGlobal(`Transaction.TransactionHistory.statuses.${data.TransactionStatus}`, {
                  defaultValue: data.TransactionStatus,
                }),
              ) || ''
            }
          />
          {data.TransactionFailureReason && (
            <Tooltip
              information={data.TransactionFailureReason}
              icon="info"
              variant="outlined"
              iconWidth="14px"
              buttonWidth="22px"
              hasButton={true}
              position="bottomLeft"
            />
          )}
        </Box>
      ),
    }));
  }, [transactionsData, currency, t, tGlobal]);

  return (
    <>
      <div className={cn('recentTransactionHeader')}>
        <Title level="5" style={{ alignSelf: 'flex-start' }}>
          {t('title')}
        </Title>
      </div>

      {fetching ? (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <Throbber />
        </div>
      ) : rows.length === 0 ? (
        <NoRecentTransactions />
      ) : (
        <Table
          id="recentTransaction"
          columns={columns}
          rows={rows}
          cellPadding="sm"
          bodyHeight="calc(100% - 50px)"
          cellWidth="190px"
        />
      )}

      <div className={cn('buttonContainer')}>
        <UtilButton
          size="large"
          onClick={() => {
            onSubNavClick(
              'transactionsHistory',
              `/transactions-history?currency=${currency}`,
              t('transactionsHistory'),
            );
          }}
        >
          {t('viewAllTransactions')}
        </UtilButton>
      </div>
    </>
  );
};

export default RecentTransactions;
